import React from 'react'
import { Box, Tooltip, SxProps, Theme } from '@mui/material'
import CustomChip from 'src/@core/components/mui/chip'
import { getApplicationStatusSubStatusAndColor } from 'src/context/common'

export interface StatusSubStatusChipProps {
  status?: string
  subStatus?: string | null
  customTitle?: React.ReactNode
  skin?: 'light'
  size?: 'small' | 'medium'
  sx?: SxProps<Theme>
  onClick?: (event: any) => void
  onDelete?: (event: any) => void
  deleteIcon?: React.ReactElement
}

const statusChipColors: Record<string, { color: string; backgroundColor: string }> = {
  warning: {
    backgroundColor: '#FFF0D4',
    color: '#BE881E'
  },
  error: {
    backgroundColor: '#FF4D4929',
    color: '#BF3A37'
  },
  secondary: {
    backgroundColor: '#262B4314',
    color: '#262B43E5'
  },
  success: {
    backgroundColor: '#00A7021F',
    color: '#00A702'
  },
  info: {
    backgroundColor: '#139F991F',
    color: '#139F99'
  },
  primary: {
    backgroundColor: '#00855429',
    color: '#008554'
  }
}

const StatusSubStatusChip: React.FC<StatusSubStatusChipProps> = ({
  status,
  subStatus,
  customTitle,
  skin = 'light',
  size = 'small',
  sx,
  onClick,
  onDelete,
  deleteIcon
}) => {
  if (!status) return <>-</>

  const { label, color } = getApplicationStatusSubStatusAndColor(status, subStatus ?? null)
  const titleText = customTitle || label
  const hasIcon = Boolean(deleteIcon || onDelete)
  const chipStyle: { color?: string; backgroundColor?: string } = (color && statusChipColors[color]) || {}

  const customSx = sx ? { ...sx } : {}
  if ((customSx as any).cursor === 'none') {
    (customSx as any).cursor = 'default'
  }

  const chipContent = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', overflow: 'hidden' }}>
      <CustomChip
        skin={skin}
        size={size}
        label={label}
        color={color}
        onClick={onClick}
        onDelete={onDelete}
        deleteIcon={deleteIcon}
        sx={{
          textTransform: 'capitalize',
          maxWidth: '100%',
          ...chipStyle,
          '&:hover, &.MuiChip-clickable:hover, &.MuiChip-root:hover': {
            ...chipStyle,
            ...(chipStyle.backgroundColor && { backgroundColor: chipStyle.backgroundColor }),
            ...(chipStyle.color && { color: chipStyle.color })
          },
          '& .MuiChip-label': {
            lineHeight: '18px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          },
          '& .MuiChip-deleteIcon, & .MuiChip-icon': {
            color: 'inherit',
            '&:hover': {
              color: 'inherit',
              opacity: 0.8
            },
            '& svg': {
              color: 'inherit',
              fill: 'currentColor'
            },
            '& path': {
              fill: 'currentColor'
            }
          },
          ...customSx
        }}
      />
    </Box>
  )

  if (hasIcon) {
    return chipContent
  }

  return (
    <Tooltip title={titleText || ''} placement='top'>
      {chipContent}
    </Tooltip>
  )
}

export default StatusSubStatusChip
